import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Calculator, 
  Send, 
  MessageCircle,
  CheckCircle,
  AlertCircle,
  FileText,
  Globe,
  Palette,
  Users,
  ArrowLeft,
  Eye,
  Sparkles
} from 'lucide-react';
import { Service, serviceApiService } from '../services/serviceApi';
import { Pack, packsApiService } from '../services/packsApi';
import { promotionApiService } from '../services/promotionApi';
import { useIrisAvatar } from '../hooks/useIrisAvatar';

interface DevisItem {
  service?: Service;
  pack?: Pack;
  type: 'service' | 'pack';
  quantity: number;
  pages?: number;
  description?: string;
}

const DevisRequestPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { avatarUrl } = useIrisAvatar();
  const [devisItems, setDevisItems] = useState<DevisItem[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [allPacks, setAllPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [showPackSelector, setShowPackSelector] = useState(false);
  const [showDevisModal, setShowDevisModal] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [savedPromoCode, setSavedPromoCode] = useState<string | null>(null);
  const [availablePromoCodes, setAvailablePromoCodes] = useState<any[]>([]);
  const [showPromoSuggestion, setShowPromoSuggestion] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  // Messages d'aide d'Iris
  const irisMessages = {
    welcome: "👋 Bonjour ! Je suis Iris, votre assistante pour créer un devis personnalisé. Je vais vous guider étape par étape !",
    selectServices: "🎯 Sélectionnez les services qui vous intéressent. Vous pouvez ajuster les quantités selon vos besoins.",
    customization: "⚙️ Parfait ! Maintenant, personnalisons chaque service selon vos besoins spécifiques.",
    finalStep: "✨ Excellent ! Il ne reste plus qu'à finaliser votre demande. Nos experts vous répondront sous 24h !"
  };

  // Vérifier les codes promo sauvegardés de l'utilisateur
  const checkSavedPromoCodes = async () => {
    try {
      // D'abord essayer de récupérer depuis le backend
      try {
        const userPromoCodes = await promotionApiService.getUserPromoCodes();
        console.log('🔍 Codes promo trouvés dans le backend:', userPromoCodes);
        
        if (userPromoCodes.length > 0) {
          // Filtrer les codes encore valides (pas expirés)
          const validCodes = userPromoCodes.filter((promo: any) => {
            if (promo.end_date) {
              const endDate = new Date(promo.end_date);
              const now = new Date();
              return endDate > now && promo.status === 'active';
            }
            return promo.status === 'active';
          });

          if (validCodes.length > 0) {
            // Stocker tous les codes valides et prendre le meilleur pour la suggestion
            setAvailablePromoCodes(validCodes);
            const bestCode = validCodes.reduce((best: any, current: any) => 
              current.discount_value > best.discount_value ? current : best
            );
            setSavedPromoCode(bestCode.code);
            setShowPromoSuggestion(true);
            console.log('🎁 Codes promo disponibles:', validCodes);
            console.log('🎁 Meilleur code promo:', bestCode);
          } else {
            console.log('⏰ Tous les codes promo ont expiré ou sont inactifs');
          }
        } else {
          console.log('📭 Aucun code promo enregistré dans le backend');
        }
      } catch (backendError) {
        console.log('⚠️ Erreur backend, fallback vers localStorage:', backendError);
        
        // Fallback vers localStorage si le backend n'est pas disponible
        const userPromoCodes = localStorage.getItem('userPromoCodes');
        if (userPromoCodes) {
          const codes = JSON.parse(userPromoCodes);
          console.log('🔍 Codes promo trouvés dans localStorage (fallback):', codes);
          
          if (codes.length > 0) {
            const validCodes = codes.filter((code: any) => {
              // Vérifier le statut actif et la date d'expiration
              const isActive = code.status === 'active';
              if (code.end_date) {
                const endDate = new Date(code.end_date);
                const now = new Date();
                return endDate > now && isActive;
              }
              return isActive;
            });

            if (validCodes.length > 0) {
              // Stocker tous les codes valides et prendre le meilleur pour la suggestion
              setAvailablePromoCodes(validCodes);
              const bestCode = validCodes.reduce((best: any, current: any) => 
                current.discount > best.discount ? current : best
              );
              setSavedPromoCode(bestCode.code);
              setShowPromoSuggestion(true);
              console.log('🎁 Codes promo disponibles (localStorage):', validCodes);
              console.log('🎁 Meilleur code promo (localStorage):', bestCode);
            }
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des codes promo:', error);
    }
  };

  useEffect(() => {
    // Charger tous les services et packs
    const loadData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Chargement des services et packs...');
        
        // Charger services et packs en parallèle
        const [services, packs] = await Promise.all([
          serviceApiService.getServices(),
          packsApiService.getPacks(true)
        ]);
        
        console.log('✅ Services chargés:', services.length, services);
        console.log('✅ Packs chargés:', packs.length, packs);
        
        setAllServices(services);
        setAllPacks(packs);
        
        // Ajouter le service ou pack pré-sélectionné s'il existe
        const selectedService = location.state?.selectedService as Service;
        const selectedPack = location.state?.selectedPack as Pack;
        
        if (selectedService) {
          console.log('📌 Service pré-sélectionné:', selectedService);
          setDevisItems([{
            service: selectedService,
            type: 'service',
            quantity: 1,
            pages: selectedService.name.toLowerCase().includes('catalogue') ? 8 : undefined,
            description: ''
          }]);
        } else if (selectedPack) {
          console.log('📌 Pack pré-sélectionné:', selectedPack);
          setDevisItems([{
            pack: selectedPack,
            type: 'pack',
            quantity: 1,
            description: ''
          }]);
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des données:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    checkSavedPromoCodes();
  }, [location.state]);

  const addService = (service: Service) => {
    const exists = devisItems.find(item => item.type === 'service' && item.service?.id === service.id);
    if (!exists) {
      setDevisItems([...devisItems, {
        service,
        type: 'service',
        quantity: 1,
        pages: service.unit === 'page' ? 8 : undefined,
        description: ''
      }]);
    }
  };

  const addPack = (pack: Pack) => {
    const exists = devisItems.find(item => item.type === 'pack' && item.pack?.id === pack.id);
    if (!exists) {
      setDevisItems([...devisItems, {
        pack,
        type: 'pack',
        quantity: 1,
        description: ''
      }]);
    }
  };

  const updateQuantity = (itemId: string, itemType: 'service' | 'pack', quantity: number) => {
    setDevisItems(items => 
      items.map(item => {
        if (itemType === 'service' && item.type === 'service' && item.service?.id === itemId) {
          return { ...item, quantity: Math.max(1, quantity) };
        }
        if (itemType === 'pack' && item.type === 'pack' && item.pack?.id === itemId) {
          return { ...item, quantity: Math.max(1, quantity) };
        }
        return item;
      })
    );
  };

  const updatePages = (serviceId: string, pages: number) => {
    setDevisItems(items => 
      items.map(item => 
        item.type === 'service' && item.service?.id === serviceId ? { ...item, pages: Math.max(1, pages) } : item
      )
    );
  };

  const updateDescription = (itemId: string, itemType: 'service' | 'pack', description: string) => {
    setDevisItems(items => 
      items.map(item => {
        if (itemType === 'service' && item.type === 'service' && item.service?.id === itemId) {
          return { ...item, description };
        }
        if (itemType === 'pack' && item.type === 'pack' && item.pack?.id === itemId) {
          return { ...item, description };
        }
        return item;
      })
    );
  };

  const removeItem = (itemId: string, itemType: 'service' | 'pack') => {
    setDevisItems(items => 
      items.filter(item => {
        if (itemType === 'service' && item.type === 'service') {
          return item.service?.id !== itemId;
        }
        if (itemType === 'pack' && item.type === 'pack') {
          return item.pack?.id !== itemId;
        }
        return true;
      })
    );
  };

  const calculateItemPrice = (item: DevisItem): number => {
    if (item.type === 'service' && item.service) {
      let basePrice = item.service.price || 0;
      if (item.service.unit === 'page' && item.pages) {
        basePrice *= item.pages;
      }
      return basePrice * item.quantity;
    }
    if (item.type === 'pack' && item.pack) {
      return item.pack.total_price * item.quantity;
    }
    return 0;
  };

  const calculateTotal = (): number => {
    return devisItems.reduce((total, item) => total + calculateItemPrice(item), 0);
  };

  const getItemName = (item: DevisItem): string => {
    if (item.type === 'service' && item.service) {
      return item.service.name;
    }
    if (item.type === 'pack' && item.pack) {
      return item.pack.name;
    }
    return 'Élément inconnu';
  };


  // Utiliser la nouvelle fonction calculateTotal définie plus haut

  const hasFixedPrices = devisItems.some(item => 
    (item.type === 'service' && item.service?.price_type === 'fixed') ||
    (item.type === 'pack')
  );
  const hasFromPrices = devisItems.some(item => 
    (item.type === 'service' && item.service?.price_type === 'from') ||
    (item.type === 'pack' && item.pack?.services?.some(service => service.price_type === 'from'))
  );

  // Vérifier si le devis est calculable (tous les éléments ont un prix défini et aucun prix variable)
  const isCalculable = devisItems.length > 0 && devisItems.every(item => {
    if (item.type === 'service' && item.service) {
      // Exclure les services avec prix "à partir de"
      return item.service.price && item.service.price > 0 && item.service.price_type !== 'from';
    }
    if (item.type === 'pack' && item.pack) {
      // Exclure les packs qui contiennent des services avec prix "à partir de"
      const hasVariablePriceServices = item.pack.services?.some(service => service.price_type === 'from');
      return item.pack.total_price && item.pack.total_price > 0 && !hasVariablePriceServices;
    }
    return false;
  });

  // Vérifier le code promo
  const checkPromoCode = async (code: string) => {
    try {
      setPromoError('');
      
      // Vérifier le code promo via l'API
      const response = await promotionApiService.validatePromoCode(code);
      
      if (response.valid && response.promotion) {
        const promotion = response.promotion;
        let discount = 0;
        
        // Extraire la valeur de réduction selon le type
        if (promotion.discount_type === 'percentage') {
          discount = Number(promotion.discount_value) || 0;
        } else if (promotion.discount_type === 'fixed_amount') {
          // Pour les montants fixes, calculer le pourcentage équivalent
          const subtotal = calculateTotal();
          const fixedAmount = Number(promotion.discount_value) || 0;
          if (subtotal > 0 && fixedAmount > 0) {
            discount = (fixedAmount / subtotal) * 100;
            console.log('💰 Montant fixe converti:', fixedAmount, 'FCFA =', discount.toFixed(2), '%');
          } else {
            discount = 0;
          }
        }
        
        // S'assurer que discount est un nombre
        discount = Number(discount) || 0;
        
        setPromoDiscount(discount);
        console.log('✅ Code promo validé:', promotion);
        console.log('✅ Réduction appliquée:', discount, '%');
        return discount;
      } else {
        setPromoError(response.error || 'Code promo invalide');
        setPromoDiscount(0);
        return 0;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du code promo:', error);
      setPromoError('Erreur lors de la vérification du code promo');
      setPromoDiscount(0);
      return 0;
    }
  };

  // Calculer le total avec réduction
  const calculateTotalWithDiscount = () => {
    const subtotal = calculateTotal();
    const discount = (subtotal * promoDiscount) / 100;
    return {
      subtotal,
      discount,
      total: subtotal - discount
    };
  };

  // Utiliser le code promo sauvegardé
  const useSavedPromoCode = async () => {
    if (savedPromoCode) {
      setPromoCode(savedPromoCode);
      await checkPromoCode(savedPromoCode);
      setShowPromoSuggestion(false);
    }
  };

  // Appliquer un code promo spécifique
  const applySpecificPromoCode = async (code: string) => {
    console.log('🎯 Application du code promo:', code);
    setPromoCode(code);
    const discount = await checkPromoCode(code);
    console.log('🎯 Réduction obtenue:', discount);
    setShowPromoSuggestion(false);
  };

  // Refuser le code promo sauvegardé
  const declineSavedPromoCode = () => {
    setShowPromoSuggestion(false);
  };

  // Fonction pour tester - effacer les codes promo (à supprimer en production)
  const clearSavedPromoCodes = () => {
    localStorage.removeItem('userPromoCodes');
    setSavedPromoCode(null);
    setAvailablePromoCodes([]);
    setShowPromoSuggestion(false);
    setPromoCode('');
    setPromoDiscount(0);
    setPromoError('');
    console.log('🧹 Codes promo effacés');
  };

  // Fonction pour tester - simuler un gain à la roue (à supprimer en production)
  const simulateWheelWin = async () => {
    try {
      // Simuler la sauvegarde d'un code promo via l'API
      // Note: Ceci nécessite une promotion existante avec l'ID 1
      await promotionApiService.saveUserPromoCode(1, 'wheel');
      console.log('🎁 Code promo test sauvegardé dans le backend');
      checkSavedPromoCodes();
    } catch (error) {
      console.error('❌ Erreur lors du test:', error);
      // Fallback vers localStorage pour les tests
      const testPromo = {
        code: 'WHEEL25',
        discount: 25,
        discount_type: 'percentage',
        source: 'wheel',
        won_date: new Date().toISOString(),
        promotion_name: 'Réduction Roue 25%',
        end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };
      
      localStorage.setItem('userPromoCodes', JSON.stringify([testPromo]));
      checkSavedPromoCodes();
      console.log('🎁 Code promo test ajouté en localStorage (fallback):', testPromo);
    }
  };

  // Gérer la demande de devis
  const handleRequestQuote = async () => {
    // Vérifier le code promo s'il y en a un et qu'il n'a pas encore été vérifié
    let finalPromoDiscount = promoDiscount;
    if (promoCode && promoDiscount === 0) {
      finalPromoDiscount = await checkPromoCode(promoCode);
    }

    // Toujours rediriger vers la page de facture proforma
    // La page de facture se chargera d'afficher les alertes appropriées pour les prix variables
    console.log('🚀 Navigation vers facture avec promoDiscount:', finalPromoDiscount);
    console.log('🚀 État actuel - promoCode:', promoCode, 'promoDiscount:', promoDiscount, 'finalPromoDiscount:', finalPromoDiscount);
    navigate('/facture-proforma', { 
      state: { 
        selectedServices: devisItems,
        promoDiscount: finalPromoDiscount,
        promoCode: promoCode
      } 
    });
  };

  const getServiceIcon = (serviceName: string) => {
    const name = serviceName.toLowerCase();
    if (name.includes('site') || name.includes('web')) return <Globe className="w-5 h-5" />;
    if (name.includes('catalogue') || name.includes('brochure')) return <FileText className="w-5 h-5" />;
    if (name.includes('design') || name.includes('logo')) return <Palette className="w-5 h-5" />;
    if (name.includes('crm') || name.includes('gestion')) return <Users className="w-5 h-5" />;
    return <CheckCircle className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header moderne */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo et retour */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png" 
                  alt="Iris Softech" 
                  className="w-12 h-12 rounded-2xl shadow-lg object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div>
                  <span className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    IRIS SOFTECH
                  </span>
                  <p className="text-xs text-gray-500 -mt-1">Solutions Digitales</p>
                </div>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="font-medium">Retour</span>
              </button>
            </div>

            {/* Titre principal */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900">Configurateur de Devis</h1>
              <p className="text-gray-500 mt-1">Personnalisez votre solution en quelques clics</p>
            </div>

            {/* Status Iris */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3 bg-gradient-to-r from-indigo-50 to-purple-50 px-4 py-2 rounded-xl border border-indigo-100">
                <div className="relative w-10 h-10 flex-shrink-0">
                  <img 
                    src={avatarUrl} 
                    alt="Iris Assistant" 
                    className="w-full h-full rounded-full shadow-md object-cover border-2 border-white"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/irisavatar.jpg';
                    }}
                  />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Iris</p>
                  <p className="text-xs text-gray-500">Assistant actif</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Colonne principale */}
          <div className="xl:col-span-3 space-y-8">
            {/* Message d'Iris moderne */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8"
            >
              <div className="flex items-center space-x-6">
                {/* Avatar d'Iris amélioré */}
                <div className="relative">
                  <img 
                    src={avatarUrl} 
                    alt="Iris Assistant" 
                    className="w-20 h-20 rounded-2xl shadow-2xl object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/irisavatar.jpg';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-2xl font-bold text-gray-900">Iris</h3>
                  </div>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {loading && "🔄 Je charge nos services pour vous... Un instant !"}
                      {!loading && allServices.length === 0 && "❌ Oups ! Je n'arrive pas à charger nos services. Vérifiez votre connexion."}
                      {!loading && allServices.length > 0 && `🎯 Parfait ! J'ai trouvé ${allServices.length} services pour vous. Cliquez sur ceux qui vous intéressent !`}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Services disponibles - Toujours visible */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Nos Services</h2>
                  <p className="text-gray-500 mt-1">Cliquez sur les services qui vous intéressent</p>
                </div>
                <div className="bg-indigo-50 px-4 py-2 rounded-xl">
                  <span className="text-indigo-700 font-semibold">{devisItems.length} sélectionné{devisItems.length > 1 ? 's' : ''}</span>
                </div>
              </div>
              
              {/* Grille des services */}
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="p-6 rounded-2xl border-2 border-gray-200 bg-white animate-pulse">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded mb-3 w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : allServices.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium mb-2">Aucun service disponible</p>
                  <p className="text-sm text-gray-400">Vérifiez votre connexion ou contactez le support</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allServices.map((service) => {
                    const isSelected = devisItems.find(item => item.type === 'service' && item.service?.id === service.id);
                    return (
                    <motion.div
                      key={service.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                        isSelected 
                          ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg' 
                          : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                      }`}
                      onClick={() => {
                        if (isSelected) {
                          removeItem(service.id, 'service');
                        } else {
                          addService(service);
                        }
                      }}
                    >
                      {isSelected && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                      
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {getServiceIcon(service.name)}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 mb-2">{service.name}</h3>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{service.description}</p>
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-bold ${
                              isSelected ? 'text-indigo-600' : 'text-gray-900'
                            }`}>
                              {service.price_type === 'fixed' && service.price 
                                ? `${service.price.toLocaleString()} FCFA`
                                : service.price_type === 'from' && service.price
                                ? `À partir de ${service.price.toLocaleString()} FCFA`
                                : service.price_type === 'per_unit' && service.price
                                ? `${service.price.toLocaleString()} FCFA${service.unit ? '/' + service.unit : ''}`
                                : 'Sur devis'
                              }
                            </span>
                            {isSelected && (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(service.id, 'service', Math.max(1, isSelected.quantity - 1));
                                  }}
                                  className="w-8 h-8 bg-indigo-100 hover:bg-indigo-200 rounded-lg flex items-center justify-center"
                                >
                                  <Minus className="w-4 h-4 text-indigo-600" />
                                </button>
                                <span className="w-8 text-center font-bold text-indigo-600">{isSelected.quantity}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(service.id, 'service', isSelected.quantity + 1);
                                  }}
                                  className="w-8 h-8 bg-indigo-100 hover:bg-indigo-200 rounded-lg flex items-center justify-center"
                                >
                                  <Plus className="w-4 h-4 text-indigo-600" />
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Sélecteur de pages pour les catalogues */}
                          {isSelected && service.unit === 'page' && (
                            <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-indigo-700">Nombre de pages</span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updatePages(service.id, Math.max(1, (isSelected.pages || 8) - 1));
                                    }}
                                    className="w-6 h-6 bg-indigo-200 hover:bg-indigo-300 rounded flex items-center justify-center"
                                  >
                                    <Minus className="w-3 h-3 text-indigo-700" />
                                  </button>
                                  <span className="w-12 text-center font-bold text-indigo-700">{isSelected.pages || 8}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updatePages(service.id, (isSelected.pages || 8) + 1);
                                    }}
                                    className="w-6 h-6 bg-indigo-200 hover:bg-indigo-300 rounded flex items-center justify-center"
                                  >
                                    <Plus className="w-3 h-3 text-indigo-700" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-xs text-indigo-600">
                                Prix: {((isSelected.pages || 8) * service.price).toLocaleString()} FCFA
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              )}

            </div>

            {/* Packs disponibles */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 mt-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Nos Packs Premium</h2>
                  <p className="text-gray-500 mt-1">Solutions complètes pour vos projets</p>
                </div>
                <div className="bg-purple-50 px-4 py-2 rounded-xl">
                  <span className="text-purple-700 font-semibold">
                    {devisItems.filter(item => item.type === 'pack').length} pack{devisItems.filter(item => item.type === 'pack').length > 1 ? 's' : ''} sélectionné{devisItems.filter(item => item.type === 'pack').length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Chargement des packs...</p>
                </div>
              ) : allPacks.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium mb-2">Aucun pack disponible</p>
                  <p className="text-sm text-gray-400">Les packs seront bientôt disponibles</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {allPacks.map((pack) => {
                    const isSelected = devisItems.find(item => item.type === 'pack' && item.pack?.id === pack.id);
                    return (
                      <motion.div
                        key={pack.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                          isSelected 
                            ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg' 
                            : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            removeItem(pack.id, 'pack');
                          } else {
                            addPack(pack);
                          }
                        }}
                      >
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        )}
                        
                        <div className="mb-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Sparkles className="w-6 h-6" />
                            </div>
                            <div className="text-right">
                              <span className={`text-lg font-bold ${
                                isSelected ? 'text-purple-600' : 'text-gray-900'
                              }`}>
                                {pack.total_price.toLocaleString()} FCFA
                              </span>
                              {pack.payment_facility && (
                                <p className="text-xs text-green-600 mt-1">{pack.payment_facility}</p>
                              )}
                            </div>
                          </div>
                          
                          <h3 className="font-bold text-gray-900 mb-2">{pack.name}</h3>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{pack.description}</p>
                          
                          {/* Services inclus */}
                          {pack.services && pack.services.length > 0 && (
                            <div className="mb-4">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Services inclus :</p>
                              <div className="flex flex-wrap gap-1">
                                {pack.services.map((service, idx) => (
                                  <span 
                                    key={idx}
                                    className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full"
                                  >
                                    {service.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Quantité */}
                          {isSelected && (
                            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                              <span className="text-sm font-medium text-gray-700">Quantité</span>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(pack.id, 'pack', Math.max(1, isSelected.quantity - 1));
                                  }}
                                  className="w-8 h-8 bg-purple-100 hover:bg-purple-200 rounded-lg flex items-center justify-center"
                                >
                                  <Minus className="w-4 h-4 text-purple-600" />
                                </button>
                                <span className="w-8 text-center font-bold text-purple-600">{isSelected.quantity}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(pack.id, 'pack', isSelected.quantity + 1);
                                  }}
                                  className="w-8 h-8 bg-purple-100 hover:bg-purple-200 rounded-lg flex items-center justify-center"
                                >
                                  <Plus className="w-4 h-4 text-purple-600" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Résumé moderne */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sticky top-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Votre Devis</h3>
                  <p className="text-sm text-gray-500">Calcul en temps réel</p>
                </div>
              </div>
              
              {devisItems.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ShoppingCart className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">Aucun élément sélectionné</p>
                  <p className="text-sm text-gray-400 mt-1">Choisissez vos services ou packs ci-dessus</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {devisItems.map((item) => {
                    const itemId = item.type === 'service' ? item.service?.id : item.pack?.id;
                    const itemName = getItemName(item);
                    const itemPrice = calculateItemPrice(item);
                    
                    return (
                      <div key={`${item.type}-${itemId}`} className="bg-gray-50 rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                              item.type === 'service' ? 'bg-indigo-100' : 'bg-purple-100'
                            }`}>
                              {item.type === 'service' 
                                ? getServiceIcon(item.service?.name || '') 
                                : <Sparkles className="w-4 h-4 text-purple-600" />
                              }
                            </div>
                            <div>
                              <span className="font-medium text-gray-900">{itemName}</span>
                              {item.type === 'pack' && (
                                <p className="text-xs text-gray-500">Pack premium</p>
                              )}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-lg text-sm font-bold ${
                            item.type === 'service' 
                              ? 'bg-indigo-100 text-indigo-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            x{item.quantity}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-gray-900">
                            {itemPrice > 0 ? `${itemPrice.toLocaleString()} FCFA` : 'Sur devis'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Suggestion de codes promo sauvegardés */}
                  {showPromoSuggestion && availablePromoCodes.length > 0 && (
                    <div className="border-t border-gray-100 pt-4 mb-4">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-200">
                        <div className="flex items-start space-x-3 mb-4">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-green-600 text-lg">🎁</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-green-800 mb-1">
                              {availablePromoCodes.length === 1 ? 'Code promo disponible !' : `${availablePromoCodes.length} codes promo disponibles !`}
                            </h4>
                            <p className="text-sm text-green-700 mb-3">
                              {availablePromoCodes.length === 1 
                                ? 'Vous avez un code promo enregistré. Voulez-vous l\'utiliser pour ce devis ?' 
                                : 'Vous avez plusieurs codes promo enregistrés. Choisissez celui que vous souhaitez utiliser :'
                              }
                            </p>
                          </div>
                        </div>
                        
                        {/* Liste des codes promo disponibles */}
                        <div className="space-y-3">
                          {availablePromoCodes.map((promo, index) => (
                            <div key={promo.code || index} className="bg-white rounded-xl p-4 border border-green-100 shadow-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-2">
                                    <h5 className="font-bold text-gray-800">{promo.name || 'Promotion'}</h5>
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                                      {promo.discount_display || `${promo.discount_type === 'percentage' ? promo.discount_value || promo.discount : promo.discount_value || promo.discount}${promo.discount_type === 'percentage' ? '%' : ' FCFA'}`}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">{promo.description}</p>
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">{promo.code}</span>
                                    {promo.end_date && (
                                      <span>Expire le {new Date(promo.end_date).toLocaleDateString('fr-FR')}</span>
                                    )}
                                    {promo.discount_type === 'buy_x_get_y' && (
                                      <span className="text-blue-600">Achetez X obtenez Y</span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => applySpecificPromoCode(promo.code)}
                                  className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                                >
                                  Utiliser
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 text-center">
                          <button
                            onClick={declineSavedPromoCode}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                          >
                            Ne pas utiliser de code promo
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Message si pas de code promo */}
                  {!showPromoSuggestion && !savedPromoCode && promoDiscount === 0 && (
                    <div className="border-t border-gray-100 pt-4 mb-4">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-200">
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-600 text-lg">🎯</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-blue-800 mb-1">Obtenez une réduction !</h4>
                            <p className="text-sm text-blue-700 mb-3">
                              Vous n'avez pas de code promo enregistré. Tentez votre chance à la roue promo pour gagner jusqu'à 50% de réduction !
                            </p>
                            <button
                              onClick={() => navigate('/#promo-wheel')}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                            >
                              🎰 Jouer à la roue
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Code promo manuel */}
                  {isCalculable && !showPromoSuggestion && (
                    <div className="border-t border-gray-100 pt-4 mb-4">
                      <div className="space-y-3">
                        <label className="text-sm font-medium text-gray-700">Code promo (optionnel)</label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="WHEEL10, WHEEL15..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                          <button
                            onClick={() => checkPromoCode(promoCode)}
                            disabled={!promoCode}
                            className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-50 text-sm font-medium"
                          >
                            Vérifier
                          </button>
                        </div>
                        {promoError && (
                          <p className="text-xs text-red-600">{promoError}</p>
                        )}
                        {promoDiscount > 0 && (
                          <p className="text-xs text-green-600">✅ Réduction de {Number(promoDiscount).toFixed(2)}% appliquée !</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t-2 border-gray-100 pt-4">
                    {promoDiscount > 0 ? (
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Sous-total</span>
                          <span>{calculateTotalWithDiscount().subtotal.toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Réduction ({Number(promoDiscount).toFixed(2)}%)</span>
                          <span>-{calculateTotalWithDiscount().discount.toLocaleString()} FCFA</span>
                        </div>
                        <div className="flex justify-between items-center border-t pt-2">
                          <span className="text-lg font-bold text-gray-900">
                            {isCalculable ? 'Total final' : 'Total provisoire'}
                          </span>
                          <span className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                            {calculateTotalWithDiscount().total.toLocaleString()} FCFA
                            {!isCalculable && <span className="text-sm text-gray-500 ml-1">*</span>}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-bold text-gray-900">Total estimé</span>
                        <span className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                          {hasFixedPrices && !hasFromPrices 
                            ? `${calculateTotal().toLocaleString()} FCFA`
                            : hasFixedPrices && hasFromPrices
                            ? `À partir de ${calculateTotal().toLocaleString()} FCFA`
                            : 'Sur devis'
                          }
                        </span>
                      </div>
                    )}
                    
                    <button 
                      onClick={handleRequestQuote}
                      disabled={devisItems.length === 0}
                      className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5" />
                      <span>Générer le devis</span>
                    </button>
                  </div>
                  
                  {hasFromPrices && (
                    <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-amber-800 mb-1">Devis provisoire disponible</p>
                          <p className="text-xs text-amber-700">
                            Vous pouvez générer un devis provisoire. Les éléments à prix variables 
                            seront révisés par nos experts qui vous contacteront sous 24h.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de devis */}
      {showDevisModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header du modal */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">Devis IRIS</h2>
                    <p className="text-indigo-100">Votre solution personnalisée</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDevisModal(false)}
                  className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Contenu du devis */}
            <div className="p-8">
              {/* Informations générales */}
              <div className="mb-8">
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <p className="text-gray-500">Date du devis</p>
                    <p className="font-semibold">{new Date().toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Référence</p>
                    <p className="font-semibold">IRIS-{Date.now().toString().slice(-6)}</p>
                  </div>
                </div>
              </div>

              {/* Éléments détaillés */}
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-6 text-gray-900">Éléments sélectionnés</h3>
                <div className="space-y-4">
                  {devisItems.map((item) => {
                    const itemId = item.type === 'service' ? item.service?.id : item.pack?.id;
                    const itemName = getItemName(item);
                    const itemPrice = calculateItemPrice(item);
                    
                    return (
                      <div key={`${item.type}-${itemId}`} className="bg-gray-50 rounded-2xl p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              item.type === 'service' ? 'bg-indigo-100' : 'bg-purple-100'
                            }`}>
                              {item.type === 'service' 
                                ? getServiceIcon(item.service?.name || '') 
                                : <Sparkles className="w-6 h-6 text-purple-600" />
                              }
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 mb-2">{itemName}</h4>
                              <p className="text-sm text-gray-600 mb-3">
                                {item.type === 'service' ? item.service?.description : item.pack?.description}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>Quantité: {item.quantity}</span>
                                {item.pages && <span>Pages: {item.pages}</span>}
                                {item.type === 'service' && item.service?.price && (
                                  <span>
                                    Prix unitaire: {item.service.price.toLocaleString()} FCFA
                                    {item.service.unit ? '/' + item.service.unit : ''}
                                  </span>
                                )}
                                {item.type === 'pack' && item.pack && (
                                  <span>Prix du pack: {item.pack.total_price.toLocaleString()} FCFA</span>
                                )}
                              </div>
                              {/* Services inclus pour les packs */}
                              {item.type === 'pack' && item.pack?.services && item.pack.services.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-semibold text-gray-700 mb-2">Services inclus :</p>
                                  <div className="flex flex-wrap gap-1">
                                    {item.pack.services.map((service, idx) => (
                                      <span 
                                        key={idx}
                                        className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full"
                                      >
                                        {service.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">
                              {itemPrice > 0 ? `${itemPrice.toLocaleString()} FCFA` : 'Sur devis'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Récapitulatif financier */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Récapitulatif</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-lg">
                    <span>Sous-total</span>
                    <span className="font-semibold">{calculateTotalWithDiscount().subtotal.toLocaleString()} FCFA</span>
                  </div>
                  {promoDiscount > 0 && (
                    <div className="flex justify-between text-lg text-green-600">
                      <span>Réduction ({Number(promoDiscount).toFixed(2)}%)</span>
                      <span className="font-semibold">-{calculateTotalWithDiscount().discount.toLocaleString()} FCFA</span>
                    </div>
                  )}
                  <div className="border-t-2 border-indigo-200 pt-3">
                    <div className="flex justify-between text-2xl font-bold">
                      <span>Total TTC</span>
                      <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                        {calculateTotalWithDiscount().total.toLocaleString()} FCFA
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-4">
                <button
                  onClick={() => window.print()}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all flex items-center justify-center space-x-2"
                >
                  <FileText className="w-5 h-5" />
                  <span>Imprimer</span>
                </button>
                <button
                  onClick={() => {
                    // Ici on pourrait envoyer le devis par email ou le sauvegarder
                    alert('Devis sauvegardé ! Vous recevrez une confirmation par email.');
                    setShowDevisModal(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Confirmer le devis</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DevisRequestPage;
